import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Card, CardContent,
  Divider, CircularProgress, Alert, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Tab, Tabs
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Edit as EditIcon,
  Delete as DeleteIcon, Warning as WarningIcon,
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [stockMovements, setStockMovements] = useState([]);

  const [formData, setFormData] = useState({
    name: '', code: '', description: '',
    product_type: 'raw_material', category: '', uom: '',
    purchase_price: 0, selling_price: 0,
    reorder_level: 0, reorder_quantity: 0,
    gst_rate: 0, is_active: true
  });

  const PRODUCT_TYPES = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'finished_good', label: 'Finished Good' },
    { value: 'by_product', label: 'By Product' },
    { value: 'packaging', label: 'Packaging' },
    { value: 'consumable', label: 'Consumable' }
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchProducts(), fetchCategories(), fetchUoms(), fetchStockMovements()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/inventory/products/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/inventory/categories/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchUoms = async () => {
    try {
      const response = await api.get('/inventory/uoms/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setUoms(data);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
    }
  };

  const fetchStockMovements = async () => {
    try {
      const response = await api.get('/inventory/stock-movements/');
      const data = Array.isArray(response.data) ? response.data
        : (response.data?.results || []);
      setStockMovements(data.slice(0, 50));
    } catch (error) {
      console.error('Error fetching stock movements:', error);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        code: product.code || '',
        description: product.description || '',
        product_type: product.product_type || 'raw_material',
        category: product.category?.id || product.category || '',
        uom: product.uom?.id || product.uom || '',
        purchase_price: parseFloat(product.purchase_price) || 0,
        selling_price: parseFloat(product.selling_price) || 0,
        reorder_level: parseFloat(product.reorder_level) || 0,
        reorder_quantity: parseFloat(product.reorder_quantity) || 0,
        gst_rate: parseFloat(product.gst_rate) || 0,
        is_active: product.is_active !== undefined ? product.is_active : true
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '', code: '', description: '',
        product_type: 'raw_material', category: '', uom: '',
        purchase_price: 0, selling_price: 0,
        reorder_level: 0, reorder_quantity: 0,
        gst_rate: 0, is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name) return toast.error('Product name is required');
      if (!formData.code) return toast.error('Product code is required');

      const submitData = {
        name: formData.name,
        code: formData.code,
        description: formData.description || '',
        product_type: formData.product_type,
        category: formData.category ? parseInt(formData.category) : null,
        uom: formData.uom ? parseInt(formData.uom) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        selling_price: parseFloat(formData.selling_price) || 0,
        reorder_level: parseFloat(formData.reorder_level) || 0,
        reorder_quantity: parseFloat(formData.reorder_quantity) || 0,
        gst_rate: parseFloat(formData.gst_rate) || 0,
        is_active: formData.is_active !== undefined ? formData.is_active : true
      };

      if (editingProduct) {
        await api.put(`/inventory/products/${editingProduct.id}/`, submitData);
        toast.success('Product updated successfully');
      } else {
        await api.post('/inventory/products/', submitData);
        toast.success('Product created successfully');
      }
      handleCloseDialog();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      if (error.response) {
        toast.error(`Failed to save: ${JSON.stringify(error.response.data)}`);
      } else {
        toast.error('Failed to save product');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.delete(`/inventory/products/${id}/`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const getStockStatus = (product) => {
    const stock = parseFloat(product.current_stock || 0);
    const reorderLevel = parseFloat(product.reorder_level || 0);
    if (stock <= 0) return { label: 'Out of Stock', color: 'error' };
    if (stock <= reorderLevel) return { label: 'Low Stock', color: 'warning' };
    return { label: 'In Stock', color: 'success' };
  };

  const getCategoryName = (product) =>
    product.category_name ||
    product.category_details?.name ||
    product.category?.name ||
    'N/A';

  const getUOMSymbol = (product) =>
    product.uom_symbol ||
    product.uom_details?.symbol ||
    product.uom?.symbol ||
    'N/A';

  const num = (v) => parseFloat(v || 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">Inventory Management</Typography>
            <Typography variant="body2" color="textSecondary">
              Manage products and track stock levels
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}>
              Add Product
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Products" />
          <Tab label="Stock Movements" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Physical stock at facility">
                          <span>At Facility</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ordered from suppliers, not yet received">
                          <span>In Procurement</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Committed to confirmed sales orders">
                          <span>Reserved (Sales)</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="At Facility minus Reserved">
                          <span>Available</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Purchase ₹</TableCell>
                      <TableCell align="right">Selling ₹</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <Typography variant="body1" color="textSecondary">
                            No products found. Click "Add Product" to add one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : products.map((product) => {
                      const status = getStockStatus(product);
                      const uom = getUOMSymbol(product);
                      const atFacility = num(product.current_stock);
                      const inProcurement = num(product.stock_in_procurement);
                      const reserved = num(product.stock_reserved_sales);
                      const available = product.available_stock !== undefined
                        ? num(product.available_stock)
                        : (atFacility - reserved);
                      const isLowStock = atFacility <= num(product.reorder_level);

                      return (
                        <TableRow key={product.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {product.code}
                            </Typography>
                          </TableCell>
                          <TableCell>{product.name}</TableCell>
                          <TableCell>
                            <Chip label={getCategoryName(product)}
                              size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={uom} size="small"
                              color="primary" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center"
                              justifyContent="flex-end" gap={1}>
                              {isLowStock && (
                                <Tooltip title="Low Stock Alert">
                                  <WarningIcon fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                              <Typography fontWeight="bold"
                                color={isLowStock ? 'warning.main' : 'text.primary'}>
                                {atFacility.toFixed(2)} {uom}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {inProcurement > 0 ? (
                              <Typography color="info.main" fontWeight="bold">
                                {inProcurement.toFixed(2)} {uom}
                              </Typography>
                            ) : (
                              <Typography color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {reserved > 0 ? (
                              <Typography color="warning.main" fontWeight="bold">
                                {reserved.toFixed(2)} {uom}
                              </Typography>
                            ) : (
                              <Typography color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color="success.main">
                              {available.toFixed(2)} {uom}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={status.label} color={status.color} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            ₹{num(product.purchase_price).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ₹{num(product.selling_price).toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary"
                              onClick={() => handleOpenDialog(product)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="error"
                              onClick={() => handleDelete(product.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Transaction</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Previous</TableCell>
                    <TableCell align="right">New</TableCell>
                    <TableCell>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockMovements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body1" color="textSecondary">
                          No stock movements found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : stockMovements.map((movement) => (
                    <TableRow key={movement.id} hover>
                      <TableCell>
                        {movement.created_at
                          ? format(new Date(movement.created_at), 'dd/MM/yyyy HH:mm')
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {movement.product_name || movement.product?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {movement.product_code || movement.product?.code || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={movement.transaction_display || movement.transaction_type}
                          size="small"
                          color={parseFloat(movement.quantity) > 0 ? 'success' : 'error'}
                          icon={parseFloat(movement.quantity) > 0
                            ? <TrendingUpIcon /> : <TrendingDownIcon />} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {parseFloat(movement.quantity) > 0 ? '+' : ''}
                        {parseFloat(movement.quantity).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(movement.previous_stock || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(movement.new_stock || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>{movement.reference || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* Product Dialog (unchanged except it no longer edits stock fields) */}
      <Dialog open={openDialog} onClose={handleCloseDialog}
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {editingProduct ? 'Edit Product' : 'Add Product'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Product Name"
                value={formData.name} onChange={handleChange('name')} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Product Code"
                value={formData.code} onChange={handleChange('code')}
                required helperText="Unique identifier" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description"
                value={formData.description}
                onChange={handleChange('description')} multiline rows={2} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Product Type</InputLabel>
                <Select value={formData.product_type}
                  onChange={handleChange('product_type')} label="Product Type">
                  {PRODUCT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select value={formData.category}
                  onChange={handleChange('category')} label="Category">
                  <MenuItem value="">Select Category</MenuItem>
                  {categories.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit of Measure</InputLabel>
                <Select value={formData.uom}
                  onChange={handleChange('uom')} label="Unit of Measure" required>
                  <MenuItem value="">Select UOM</MenuItem>
                  {uoms.map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name} ({u.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={formData.is_active}
                  onChange={handleChange('is_active')} label="Status">
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Pricing & Reorder
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Purchase Price (₹)" type="number"
                value={formData.purchase_price}
                onChange={handleChange('purchase_price')}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Selling Price (₹)" type="number"
                value={formData.selling_price}
                onChange={handleChange('selling_price')}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reorder Level" type="number"
                value={formData.reorder_level}
                onChange={handleChange('reorder_level')}
                helperText="Minimum stock level before reorder"
                InputProps={{ inputProps: { min: 0, step: 1 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reorder Quantity" type="number"
                value={formData.reorder_quantity}
                onChange={handleChange('reorder_quantity')}
                helperText="Quantity to reorder"
                InputProps={{ inputProps: { min: 0, step: 1 } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="GST Rate (%)" type="number"
                value={formData.gst_rate}
                onChange={handleChange('gst_rate')}
                InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }} />
            </Grid>
            {editingProduct && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Stock buckets (At Facility / In Procurement / Reserved / Available)
                  are updated automatically by procurement, sales, and production flows.
                  They cannot be edited manually.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProduct ? 'Update' : 'Add'} Product
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Inventory;